<div class="employee-form core-screen">
  <div class="employee-form__main-content">
    @if (isLoading()) {
      <div class="employee-form__loading">
        {{ 'common.loading' | translate }}
      </div>
    }

    <form
      [formGroup]="form"
      class="employee-form__form"
      [class.employee-form__form--loading]="isLoading()"
    >
      <div class="employee-form__photo-block">
        <div class="employee-form__avatar-wrap">
          <div
            class="employee-form__avatar"
            [class.employee-form__avatar--has-image]="!!displayPhotoUrl()"
          >
            @if (displayPhotoUrl(); as photoSrc) {
              <img
                class="employee-form__avatar-img"
                [src]="photoSrc"
                alt=""
                loading="lazy"
                decoding="async"
              />
            } @else {
              <svg
                class="employee-form__avatar-placeholder"
                width="40"
                height="40"
                viewBox="0 0 24 24"
                fill="none"
                aria-hidden="true"
              >
                <circle cx="12" cy="8" r="4" stroke="white" stroke-width="2" />
                <path
                  d="M6 21c0-3.314 2.686-6 6-6s6 2.686 6 6"
                  stroke="white"
                  stroke-width="2"
                  stroke-linecap="round"
                />
              </svg>
            }
          </div>
          @if (photoFile()) {
            <button
              type="button"
              class="employee-form__avatar-clear"
              (click)="clearPendingPhoto()"
              [attr.aria-label]="'admin.employees.photo.clearPending' | translate"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path
                  d="M18 6L6 18M6 6l12 12"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                />
              </svg>
            </button>
          }
          <button
            type="button"
            class="employee-form__avatar-camera"
            (click)="onPickPhoto()"
            [disabled]="isLoading()"
            [attr.aria-label]="'admin.employees.create.photoHint' | translate"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path
                d="M23 19a2 2 0 01-2 2H3a2 2 0 01-2-2V8a2 2 0 012-2h4l2-3h6l2 3h4a2 2 0 012 2v11z"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <circle cx="12" cy="13" r="4" stroke="currentColor" stroke-width="2" />
            </svg>
          </button>
        </div>
        <p class="employee-form__photo-hint">
          {{ 'admin.employees.create.photoHint' | translate }}
        </p>
      </div>

      <lib-not-input
        type="text"
        formControlName="lastName"
        [label]="'admin.employees.create.lastNameLabel' | translate"
        [required]="true"
        [placeholder]="'admin.employees.create.lastNameExample' | translate"
        [error]="getFieldError('lastName') | translate"
        [height]="'56px'"
      />

      <lib-not-input
        type="text"
        formControlName="firstName"
        [label]="'admin.employees.create.firstNameLabel' | translate"
        [required]="true"
        [placeholder]="'admin.employees.create.firstNameExample' | translate"
        [error]="getFieldError('firstName') | translate"
        [height]="'56px'"
      />

      <lib-not-input
        type="text"
        formControlName="patronymic"
        [label]="'admin.employees.create.patronymicLabel' | translate"
        [required]="true"
        [placeholder]="'admin.employees.create.patronymicExample' | translate"
        [error]="getFieldError('patronymic') | translate"
        [height]="'56px'"
      />

      <lib-not-phone-input
        formControlName="phone"
        [countrySelectionEnabled]="true"
        [label]="'admin.employees.create.phoneLabel' | translate"
        [required]="true"
        [placeholder]="'(29) 111-11-11' | translate"
        [error]="phoneControlErrorText()"
        [height]="'56px'"
        (trigger)="unfocusedPhoneInput.next(true)"
      />

      <lib-not-input
        type="email"
        formControlName="email"
        [label]="'admin.employees.create.emailLabel' | translate"
        [placeholder]="'example@mail.com' | translate"
        [error]="getFieldError('email') | translate"
        [height]="'56px'"
      />

      <lib-not-input
        type="text"
        formControlName="telegramLink"
        [label]="'admin.employees.create.telegramLabel' | translate"
        [placeholder]="'https://t.me/username' | translate"
        [height]="'56px'"
      />

      <lib-not-input
        type="text"
        formControlName="position"
        [label]="'admin.employees.create.positionLabel' | translate"
        [required]="true"
        [placeholder]="'admin.employees.create.positionExample' | translate"
        [error]="getFieldError('position') | translate"
        [height]="'56px'"
      />

      <lib-not-input
        type="text"
        formControlName="actsOnBasis"
        [label]="'admin.employees.create.actsOnBasisLabel' | translate"
        [placeholder]="'admin.employees.create.actsOnBasisExample' | translate"
        [error]="getFieldError('actsOnBasis') | translate"
        [height]="'56px'"
      />

      <label class="employee-form__select-wrap">
        <span class="employee-form__label">{{
          'admin.employees.create.warehouseLabel' | translate
        }}</span>
        <select
          class="employee-form__select"
          formControlName="warehouseId"
          [disabled]="isWarehousesLoading()"
        >
          <option value="">
            {{ 'admin.employees.create.warehousePlaceholder' | translate }}
          </option>
          @for (warehouse of warehouseOptions(); track warehouse.id) {
            <option [value]="warehouse.id">{{ warehouse.label }}</option>
          }
        </select>
      </label>

      <lib-not-input
        #rolesDropdown
        type="dropdown"
        [options]="availableRoles()"
        [multiSelect]="true"
        [label]="'admin.employees.create.rolesLabel' | translate"
        [required]="true"
        [placeholder]="'admin.employees.create.rolesPlaceholder' | translate"
        [error]="getFieldError('roles') | translate"
        (dropdownChange)="onRolesChange($event)"
        [height]="'56px'"
      />
    </form>
  </div>

  <div class="employee-form__actions">
    <lib-cabinet-button
      type="submit"
      variant="primary"
      size="lg"
      [fullWidth]="true"
      [disabled]="isSubmitDisabled()"
      [isLoading]="isSubmitting()"
      [text]="(isEditMode() ? 'common.actions.update' : 'common.actions.create') | translate"
      [action]="{ type: 'action' }"
      (actionEvent)="onSubmit()"
    />
  </div>
</div>
